<template>
  <div class="container">
    <donut-page-title :title="pointsName + ' Leaderboard'" />
    <div v-if="state.currentUser.paid_points_admin" class="f-as m-tx4">
      <i class="mi md-18 gray">info</i>
      <donut-rich-text v-model="adminInfoText" class="m-lx1" />
    </div>
    <div v-if="tagsCards.length" class="m-tx5">
      <h1 class="t t-xl m-bx2">Top {{ shoutoutsName }} with Value Receivers</h1>
      <div class="f f-gap-40 f-w">
        <shoutouts-leaderboard-card
          v-for="(card, cIdx) in tagsCards"
          :key="`tags-card-${cIdx}`"
          :card="card"
          :config="props.config"
        />
      </div>
    </div>
    <div v-if="leaderCards.length" class="m-tx5">
      <h1 class="t t-xl m-bx2">Points</h1>
      <div v-if="leaderCards.length" class="f f-gap-40">
        <shoutouts-leaderboard-card
          v-for="(card, cIdx) in leaderCards"
          :key="`leader-card-${cIdx}`"
          class="w-x32 h-x20 p-x2 b-2 br-8"
          :card="card"
          :config="config"
        />
      </div>
    </div>
    <shoutouts-leaderboard-table class="m-tx5" :config="props.config" :headers="headers" />
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue';
  import { useStore } from 'vuex';

  const { state, commit } = useStore();

  const props = defineProps({
    config: { type: Object, required: true },
    rewards: { type: Object, default: null },
    headers: { type: Array, required: true },
    topGivers: { type: Array, default: null },
    topReceivers: { type: Array, default: null },
    topIntros: { type: Array, default: null },
    topTagsReceivers: { type: Array, default: () => [] },
  });
  const $i18n = inject('$i18n');

  const pointsName = _.startCase($i18n.t('shoutouts.point', { count: 2 }));
  const shoutoutsName = _.startCase($i18n.t('shoutouts.shoutout', { count: 2 }));

  const adminInfoText = computed(() => {
    const columnsToMention = [];
    if (props.headers.some(h => h.key === 'received')) columnsToMention.push('Shoutouts Received');
    if (props.headers.some(h => h.key === 'redeemed')) columnsToMention.push('Redeemed');

    const sections = [
      'You are viewing this page as a ',
      { type: 'link', text: `Donut ${pointsName} Admin`, href: '/points/permissions', target: '_blank' },
      '. Non-admins cannot see any of the ',
      { text: 'All-Time Boxes', class: 'bold' },
      ' or the ',
    ];
    columnsToMention.forEach((label, idx) => {
      if (idx > 0) sections.push(' or ');
      sections.push({ text: label, class: 'bold' });
    });
    sections.push(` column${columnsToMention.length > 1 ? 's' : ''}`);
    if (state.currentUser.paid_shoutouts_admin) sections.push(', and can not click on a user\'s name to view their profile');
    sections.push('.');
    return sections;
  });

  const topUsersToListings = (users) => {
    const ranks = [
      { num: 1, iconStyle: { color: '#CFAB10', 'background-color': '#EFD358' } },
      { num: 2, iconStyle: { color: '#A6A6A5', 'background-color': '#DCDCDC' } },
      { num: 3, iconStyle: { color: '#BB7307', 'background-color': '#D69A41' } },
    ];
    const list = [];
    [0, 1, 2].forEach((wIdx) => {
      const listing = {};
      if (users[wIdx]) {
        listing.score = users[wIdx].points;
        listing.rank = listing.score === list[wIdx - 1]?.score ? list[wIdx - 1].rank : ranks[wIdx];
        listing.name = users[wIdx].name;
        listing.avatar = users[wIdx].avatar;
      } else {
        listing.name = 'Nobody yet!';
        listing.rank = ranks[wIdx];
      }
      list.push(listing);
    });

    if (users[3] && users[3].points === list[2].score) {
      // If the last visible ranking is a tie with the following users, don't show any names for that ranking
      list.forEach((listing) => {
        if (listing.score === list[2].score) {
          listing.name = 'It\'s a tie!';
          listing.avatar = null;
        }
      });
    }

    return list;
  };

  const tagsCards = computed(() => props.topTagsReceivers.map(({ tag, topReceivers }) => ({
    titleEmoji: tag.emoji,
    title: `#${tag.hashtag}`,
    description: tag.description,
    list: topUsersToListings(topReceivers),
  })));

  const leaderCards = computed(() => {
    const cards = [
      { title: `All-Time ${shoutoutsName} Given`, propName: 'topGivers' },
      { title: `All-Time ${shoutoutsName} Received`, propName: 'topReceivers' },
      { title: 'All-Time Intros Received', propName: 'topIntros' },
    ];
    return cards.map(card => {
      const users = props[card.propName];
      if (!users) return;

      return {
        ...card,
        list: topUsersToListings(users),
      };
    }).filter(card => card);
  });
</script>
